import React, { Component } from "react";
import styled from "styled-components";
import tinycolor from "tinycolor2";

import { themeColors } from "js/react/sharedStyles";
import { uppercaseTitle } from "js/textStyles";

import Spinner from "js/react/components/Spinner";
import { Icon } from "./Icon";
import { DisabledStateToolTip } from "./ToolTip";

interface StyledButtonProps {
    backgroundColor?: string;
    color?: string;
    iconColor?: string;
    hoverColor?: string;
    rounded?: boolean;
    size?: "small" | "large" | "xlarge" | null;
    width?: number | string;
    height?: number;
    unframed?: boolean;
    textColor?: string;
}

const StyledButton = styled.button<StyledButtonProps>`
    pointer-events: auto;
    cursor: pointer;
    background: ${props => props.backgroundColor};
    color: ${props => props.textColor};
    border-radius: ${props => props.rounded ? "18px" : "2px"};
    border: none;
    font-size: ${props => {
        if (props.size == "xlarge") {
            return 16;
        } else if (props.size == "large") {
            return 14;
        } else if (props.size == "small") {
            return 11;
        } else {
            return 12;
        }
    }}px;
    font-family: Source Sans Pro;
    font-weight: 600;
    letter-spacing: 0.025em;
    width: ${props => props.width ? (typeof props.width === "number" ? props.width + "px" : props.width) : "auto"};
    height: ${props => props.height || 36}px;

    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${props => props.size == "xlarge" ? "20px 26px" : props.size == "large" ? "10px 20px" : "6px 12px"};
    position: relative;

    &:before {
        content: "";
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background: ${props => props.hoverColor ?? "black"};
        border-radius: ${props => props.rounded ? "18px" : "2px"};
        opacity: 0;
        z-index: 0;
        transition: opacity 0.2s ease-in-out;
    }

    span {
        z-index: 1;
        text-transform: uppercase;
    }

    .icon {
        z-index: 1;
    }

    img {
        z-index: 1;
    }

    img + span {
        margin-left: 8px;
    }

    .icon + span {
        //margin-left: 8px;
        margin-left: ${props => props.size == "small" ? 5 : 8}px;
    }

    .bai-icon {
        color: ${props => props.iconColor} !important;
    }

    &:hover {
        &:before {
            opacity: ${props => props.hoverColor ? 1 : 0.2};
            background: ${props => props.hoverColor ?? "black"};
        }
    }

    &:disabled {
        opacity: 0.5;
    }
`;

interface ButtonProps {
    large?: boolean;
    rounded?: boolean;
    unframed?: boolean;
    style?: React.CSSProperties;
    small?: boolean;
    blue?: boolean;
    warning?: boolean;
    width?: number | string;
    height?: number;
    disabledTooltip?: string;
    color?: string;
    allowTabKey?: boolean;
    children: React.ReactNode | string;

    [key: string]: any;
}

export class Button extends Component<ButtonProps> {
    render() {
        let { showSpinner, outline, solid, large, xlarge, rounded = false, unframed = false, style = {}, small, blue, ai, warning, upgrade, width, height, disabledTooltip, className, allowTabKey, children, ...rest } = this.props;

        let flatChildren = React.Children.toArray(children).reduce<React.ReactNode[]>((flat, child) => {
            if (React.isValidElement(child) && child.type === React.Fragment) {
                return flat.concat(React.Children.toArray(child.props.children));
            }
            return flat.concat(child);
        }, []);

        let IconChild = flatChildren.find(child =>
            React.isValidElement(child) && (
                child.type === Icon || (child.type as any).name == "AiIcon" ||
                child.type === "img" ||
                ((child.type as any)?.upgradeIcon === true)
            )
        );
        let LabelChildren = flatChildren.filter(child => child !== IconChild);

        let backgroundColor = "#d1d1d1";
        let textColor = "white";
        let iconColor = "white";
        let hoverColor;

        if (unframed) {
            backgroundColor = "transparent";
            const isDestructive = warning || rest.color === themeColors.warning;
            if (isDestructive) {
                textColor = themeColors.warning;
                iconColor = themeColors.warning;
                hoverColor = tinycolor(themeColors.warning).setAlpha(0.12).toRgbString();
            } else {
                textColor = blue ? themeColors.ui_blue : "#333";
                iconColor = themeColors.ui_blue;
                hoverColor = themeColors.selection;
            }
            style.justifyContent = "flex-start";
        } else if (blue) {
            backgroundColor = themeColors.ui_blue;
        } else if (ai) {
            backgroundColor = themeColors.aiColor;
        } else if (warning) {
            backgroundColor = themeColors.warning;
        } else if (upgrade) {
            backgroundColor = "#FA901F";
        } else if (rest.color) {
            backgroundColor = rest.color;
            hoverColor = tinycolor(backgroundColor).darken(10).toString();
        } else {
            backgroundColor = themeColors.rollover;
            textColor = "rgba(0,0,0,0.76)";
            iconColor = "rgba(0,0,0,0.76)";
            style.border = `solid 1px ${themeColors.ui_blue}`;
            hoverColor = themeColors.selection;
        }

        let size = null;
        if (large) {
            size = "large";
        } else if (xlarge) {
            size = "xlarge";
        } else if (small) {
            size = "small";
        }

        if (backgroundColor === "#FFF") {
            textColor = "#333";
        }

        return (
            <DisabledStateToolTip {...this.props}>
                <StyledButton className={`bai-button ${className ?? ""}`}
                              width={width} height={height}
                              backgroundColor={backgroundColor}
                              textColor={textColor}
                              iconColor={iconColor}
                              hoverColor={hoverColor}
                              size={size}
                              unframed={unframed}
                              rounded={rounded}
                              style={style}
                              // onMouseDown={event => event.preventDefault()}
                              onKeyDown={event => event.preventDefault()}
                              {...rest}
                >
                    {showSpinner && <Spinner color={textColor} small />}
                    {/*<FlexBox style={{ opacity: showSpinner ? 0 : 1 }}>*/}
                    {IconChild && IconChild}
                    {LabelChildren.length > 0 && <span>{LabelChildren}</span>}
                    {/*</FlexBox>*/}
                </StyledButton>
            </DisabledStateToolTip>
        );
    }
}

const dialogButtonStyles = `
    ${uppercaseTitle}
    line-height: 24px;
    text-align: center;
    letter-spacing: 0.5px;
    border-radius: 2px;
    gap: 6px;
    text-wrap: nowrap;
    padding: 6px 16px;
    background-color: ${themeColors.ui_blue};
    color: white;
`;

export const DialogButton = styled(StyledButton)`
    ${dialogButtonStyles}
`;

export const TextDialogButton = styled(StyledButton)`
    ${dialogButtonStyles}
    background: transparent;
    color: ${themeColors.ui_blue};
    border: none;

    &:hover {
        &:before {
            opacity: 0.15;
            background: ${themeColors.ui_blue};
        }
    }
`;

